import {yupResolver} from "@hookform/resolvers/yup";
import {Button, TextField, Stack, Autocomplete, Select, MenuItem, FormControl, FormLabel} from "@mui/material";
import {useForm, Controller} from "react-hook-form";
import * as yup from "yup";
import useApiRequest from "../../hooks/useApiRequest";
import {snackbar} from "../../utils/snackbar";
import {useNavigate} from "react-router-dom";
import {useEffect, useState, useRef} from "react";

// Opciones disponibles según la API
const SCOPE_OPTIONS = {
  action: ["read", "write", "delete"],
  ownership: ["global", "tenant", "self"]
};

const schema = yup.object().shape({
  resource: yup
    .string()
    .required("Selecciona el recurso")
    .min(1, "Mínimo 1 carácter")
    .max(100, "Máximo 100 caracteres")
    .matches(/^\S+$/, "No puede contener espacios"),

  subresource: yup
    .string()
    .nullable()
    .notRequired()
    .max(100, "Máximo 100 caracteres")
    .test("no-spaces", "No puede contener espacios", function (value) {
      if (!value) return true; // Permitir null/vacío
      return /^\S+$/.test(value);
    }),

  action: yup
    .string()
    .required("Selecciona la acción")
    .min(1, "Mínimo 1 carácter")
    .max(50, "Máximo 50 caracteres")
    .matches(/^\S+$/, "No puede contener espacios"),

  ownership: yup
    .string()
    .required("Selecciona el alcance")
    .oneOf(["global", "tenant", "self"], "Debe ser global, tenant o self"),

  description: yup.string().nullable().max(255, "Máximo 255 caracteres"),

  product: yup.mixed().required("Selecciona un producto")
});

const ScopeForm = ({isNew, refreshData, setIsEditing, initialScopeData}) => {
  const navigate = useNavigate();
  const {loading, create, update} = useApiRequest({baseUrl: `/scopes`, resource: "scopes"});
  const {makeRequest} = useApiRequest({baseUrl: `/products`, resource: "products"});

  const [productOptions, setProductOptions] = useState([]);

  useEffect(() => {
    const fetchProducts = async () => {
      const result = await makeRequest("get", "");

      if (result?.data?.data) {
        const mapped = result.data.data.map((p) => ({id: p.id, label: p.name || p.id, ...p}));
        setProductOptions(mapped);
      }
    };
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async (data) => {
    const productIdValue = data.product?.id ?? null;

    const submitData = {
      product_id: productIdValue,
      resource: data.resource,
      subresource: data.subresource || null,
      action: data.action,
      ownership: data.ownership,
      description: data.description || null
    };

    const result = isNew ? await create(submitData) : await update(initialScopeData.id, submitData);
    if (!result?.success) return;

    if (isNew) {
      snackbar.success("Scope creado correctamente.");
      navigate("/scopes");
      return;
    }

    snackbar.success("Scope actualizado correctamente.");
    await refreshData();
    setIsEditing(false);
  };

  const {
    register,
    handleSubmit,
    formState: {errors},
    control,
    reset
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      resource: "",
      subresource: "",
      action: "",
      ownership: "",
      description: initialScopeData?.description || "",
      product: null
    }
  });

  const prevIdRef = useRef(null);

  useEffect(() => {
    if (productOptions.length === 0 || !initialScopeData?.id) {
      return;
    }

    // Solo ejecutar si el ID cambió
    if (prevIdRef.current === initialScopeData.id) {
      return;
    }

    prevIdRef.current = initialScopeData.id;

    const selectedProduct = initialScopeData?.product
      ? productOptions.find((p) => p.id === initialScopeData.product)
      : null;

    reset({
      resource: initialScopeData.resource || "",
      subresource: initialScopeData.subresource || "",
      action: initialScopeData.action || "",
      ownership: initialScopeData.ownership || "",
      description: initialScopeData.description || "",
      product: selectedProduct || null
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productOptions]);

  const onSubmit = (data) => {
    handleSave(data);
  };

  return (
    <Stack direction="column" spacing={2} minWidth={"300px"}>
      <TextField
        label="Recurso *"
        {...register("resource")}
        error={!!errors.resource}
        helperText={errors.resource?.message || "Sin espacios"}
        variant="outlined"
        size="small"
      />

      <TextField
        label="Subrecurso (Opcional)"
        {...register("subresource")}
        error={!!errors.subresource}
        helperText={errors.subresource?.message || "Sin espacios"}
        variant="outlined"
        size="small"
      />

      <FormControl fullWidth error={!!errors.action}>
        <Controller
          name="action"
          control={control}
          render={({field}) => (
            <Autocomplete
              freeSolo
              options={SCOPE_OPTIONS.action}
              value={field.value}
              onInputChange={(_, value) => field.onChange(value)}
              onChange={(_, value) => field.onChange(value ?? "")}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Acción *"
                  error={!!errors.action}
                  helperText={errors.action?.message || "Sin espacios"}
                  variant="outlined"
                  size="small"
                />
              )}
            />
          )}
        />
      </FormControl>

      <FormControl fullWidth error={!!errors.ownership}>
        <FormLabel>Alcance (Ownership) *</FormLabel>
        <Controller
          name="ownership"
          control={control}
          render={({field}) => (
            <Select {...field} variant="outlined" size="small">
              <MenuItem value="">Selecciona</MenuItem>
              {SCOPE_OPTIONS.ownership.map((option) => (
                <MenuItem key={option} value={option}>
                  {option === "global"
                    ? "Global (cualquier recurso)"
                    : option === "tenant"
                      ? "Tenant (de tu empresa)"
                      : "Self (tu propio recurso)"}
                </MenuItem>
              ))}
            </Select>
          )}
        />
        {errors.ownership && <span style={{color: "red", fontSize: "0.75rem"}}>{errors.ownership.message}</span>}
      </FormControl>

      <TextField
        label="Descripción (Opcional)"
        {...register("description")}
        multiline
        rows={2}
        slotProps={{htmlInput: {maxLength: 255}}}
        error={!!errors.description}
        helperText={errors.description?.message}
        variant="outlined"
      />

      <Controller
        name="product"
        control={control}
        render={({field}) => (
          <Autocomplete
            {...field}
            options={productOptions}
            getOptionLabel={(option) => option?.label || ""}
            onChange={(_, value) => field.onChange(value)}
            disableCloseOnSelect={false}
            autoHighlight
            renderInput={(params) => (
              <TextField
                {...params}
                label="Producto *"
                error={!!errors.product}
                helperText={errors.product?.message}
                variant="outlined"
              />
            )}
          />
        )}
      />

      <Button variant="contained" color="primary" onClick={handleSubmit(onSubmit)} disabled={loading}>
        {isNew ? "Crear Scope" : "Guardar Cambios"}
      </Button>
    </Stack>
  );
};

export default ScopeForm;
